'use strict';

const GOOGLE_MAPS_LOAD_PROMISE_KEY = '__googleMapsLoadPromise';

let geocoder;

/**
 * Returns true when the Google Maps API is available on the page.
 * @returns {boolean}
 */
function isGoogleMapsLoaded() {
    return !!(window.google && window.google.maps);
}

/**
 * Loads the Google Maps API once. Resolves immediately if already present.
 * @param {string} googleKey google api key
 * @param {string|null} integrity optional SHA-384 SRI hash for the Maps script
 * @returns {Promise<void>}
 */
function includeGoogleMaps(googleKey, integrity = null) {
    if (isGoogleMapsLoaded()) {
        return Promise.resolve();
    }

    if (!googleKey) {
        return Promise.reject(new Error('Google Maps API key is missing'));
    }

    if (window[GOOGLE_MAPS_LOAD_PROMISE_KEY]) {
        return window[GOOGLE_MAPS_LOAD_PROMISE_KEY];
    }

    window[GOOGLE_MAPS_LOAD_PROMISE_KEY] = new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');

        if (existingScript) {
            if (isGoogleMapsLoaded()) {
                resolve();
                return;
            }

            existingScript.addEventListener('load', () => resolve());
            existingScript.addEventListener('error', reject);
            return;
        }

        const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${googleKey}`;
        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.crossOrigin = 'anonymous';
        if (integrity) {
            script.integrity = integrity;
        }
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return window[GOOGLE_MAPS_LOAD_PROMISE_KEY];
}

/**
 * This method renders location from google maps based on address
 * @param {function} callBack callback function
 * @param {Object} mixin additional parameters
 */
function getLatLong(callBack, mixin = {}) {
    const { address } = mixin;
    geocoder = new google.maps.Geocoder();
    if (!(geocoder && address)) return;
    const data = Object.assign({}, mixin);
    geocoder.geocode({ address }, function (results, status) {
        if (status === google.maps.GeocoderStatus.OK) {
            const { location } = results[0].geometry;
            data.latitude = location.lat();
            data.longitude = location.lng();
        }
        callBack(data);
    });
}

export { includeGoogleMaps, getLatLong };
